import { useEffect, useRef } from 'react';
import type { QuestionMeta } from '../../hooks/useExamSession';

/**
 * How long each question was on screen: closed visits add up, and the open one is added when it ends. A tab in the
 * background does not count. Reported as a running total (the server only raises it), also whenever the session is
 * about to send, so the visit still open when a section is closed is not lost.
 */
export function useQuestionTime(
  openQuestionId: string | undefined,
  onMeta?: (questionId: string, meta: QuestionMeta) => void,
  registerSettle?: (settle: () => void) => () => void,
): void {
  const spentMs = useRef(new Map<string, number>());
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;
  const registerRef = useRef(registerSettle);
  registerRef.current = registerSettle;

  useEffect(() => {
    if (!openQuestionId) return undefined;
    let openedAt: number | null = null;
    const totalMs = () => (spentMs.current.get(openQuestionId) ?? 0) + (openedAt === null ? 0 : Date.now() - openedAt);
    const report = () => onMetaRef.current?.(openQuestionId, { timeSpentSec: Math.round(totalMs() / 1000) });
    const open = () => {
      if (openedAt === null && document.visibilityState !== 'hidden') openedAt = Date.now();
    };
    const close = () => {
      if (openedAt === null) return;
      const total = totalMs();
      openedAt = null;
      spentMs.current.set(openQuestionId, total);
      report();
    };
    const onVisibility = () => (document.visibilityState === 'hidden' ? close() : open());
    open();
    document.addEventListener('visibilitychange', onVisibility);
    const unregister = registerRef.current?.(() => {
      if (openedAt !== null) report();
    });
    return () => {
      unregister?.();
      document.removeEventListener('visibilitychange', onVisibility);
      close();
    };
  }, [openQuestionId]);
}
